import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, Droplets, Star, Crown, Car, Sailboat, Truck, Volume2, VolumeX } from "lucide-react";
import { SLOGANS, BRAND } from "@/components/shared/BrandComponents";
import soundEffects from "@/utils/soundEffects";

// Floating Slogan Ticker - Smooth infinite scroll with sound effects
export const SloganTicker = ({ speed = "normal", variant = "default" }) => {
  const { t } = useTranslation();
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const tickerRef = useRef(null);

  // Initialize sound on first user interaction
  const initSound = () => {
    if (!isInitialized) {
      soundEffects.init();
      setIsInitialized(true);
    }
  };

  const handleHover = () => {
    initSound();
    if (soundEnabled) {
      soundEffects.playHover();
    }
  };

  const handleClick = () => {
    initSound();
    if (soundEnabled) {
      soundEffects.playSparkle();
    }
  };

  const toggleSound = (e) => {
    e.stopPropagation();
    initSound();
    setSoundEnabled(!soundEnabled);
    soundEffects.setEnabled(!soundEnabled);
    if (!soundEnabled) {
      soundEffects.playNotification();
    }
  };

  const speedClass = {
    slow: "animate-scroll-slow",
    normal: "animate-scroll",
    fast: "animate-scroll-fast",
  }[speed];

  const tickerItems = [
    { type: "slogan1", text: SLOGANS.secondary, color: "text-amber-400" },
    { type: "icon", icon: Sparkles, color: "text-amber-400" },
    { type: "slogan2", text: SLOGANS.primary, color: "text-cyan-400" },
    { type: "icon", icon: Droplets, color: "text-cyan-400" },
    { type: "service", text: "Car Wash", icon: Car, color: "text-cyan-400" },
    { type: "icon", icon: Star, color: "text-amber-400" },
    { type: "service", text: "Boat Detailing", icon: Sailboat, color: "text-blue-400" },
    { type: "icon", icon: Crown, color: "text-amber-400" },
    { type: "service", text: "RV Washing", icon: Truck, color: "text-purple-400" },
    { type: "icon", icon: Sparkles, color: "text-cyan-400" },
    { type: "brand", text: BRAND.website, color: "text-cyan-400" },
    { type: "icon", icon: Star, color: "text-amber-400" },
  ];

  // Duplicate items for seamless loop
  const allItems = [...tickerItems, ...tickerItems, ...tickerItems];

  if (variant === "minimal") {
    return (
      <div 
        ref={tickerRef}
        className="overflow-hidden bg-slate-900/80 border-y border-white/5 py-2 cursor-pointer relative group"
        onMouseEnter={handleHover}
        onClick={handleClick}
      >
        {/* Sound Toggle Button */}
        <button
          onClick={toggleSound}
          className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-1.5 rounded-full bg-slate-800/80 text-slate-400 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
          title={soundEnabled ? t("slogan_ticker.mute_sounds") : t("slogan_ticker.enable_sounds")}
        >
          {soundEnabled ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}
        </button>

        <div className={`flex w-max ${speedClass}`}>
          {allItems.map((item, tickerIdx) => (
            <div key={`ticker-${tickerIdx}`} className="flex shrink-0 items-center gap-3 px-4 whitespace-nowrap">
              {item.type === "icon" ? (
                <item.icon className={`w-4 h-4 ${item.color}`} />
              ) : item.type === "service" ? (
                <span className={`flex items-center gap-2 ${item.color} text-sm font-medium`}>
                  <item.icon className="w-4 h-4" />
                  {item.text}
                </span>
              ) : (
                <span className={`${item.color} text-sm font-bold tracking-wider uppercase`}>
                  {item.text}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div 
      data-testid="public-slogan-ticker"
      className="overflow-hidden bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-y border-amber-500/20 py-2.5 cursor-pointer relative group"
      onMouseEnter={handleHover}
      onClick={handleClick}
    >
      {/* Sound Toggle Button */}
      <button
        onClick={toggleSound}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-1.5 rounded-full bg-slate-800/80 text-slate-400 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
        title={soundEnabled ? t("slogan_ticker.mute_sounds") : t("slogan_ticker.enable_sounds")}
      >
        {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
      </button>

      <div className={`flex w-max ${speedClass}`}>
        {allItems.map((item, tickerIdx) => (
          <div key={`ticker-${tickerIdx}`} className="flex shrink-0 items-center gap-4 px-6 whitespace-nowrap">
            {item.type === "icon" ? (
              <item.icon className={`w-4 h-4 ${item.color} animate-pulse`} />
            ) : item.type === "service" ? (
              <span className={`flex items-center gap-2 ${item.color} text-sm font-medium`}>
                <item.icon className="w-4 h-4" />
                {item.text}
              </span>
            ) : item.type === "brand" ? (
              <span className={`${item.color} text-sm font-semibold`}>
                {item.text}
              </span>
            ) : (
              <span className={`${item.color} text-sm font-bold tracking-wider uppercase`}>
                {item.text}
              </span>
            )}
            <span className="text-slate-600">•</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Premium Gradient Ticker with glow effect and sound
export const PremiumTicker = ({ speed = "normal" }) => {
  const { t } = useTranslation();
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  const initSound = () => {
    if (!isInitialized) {
      soundEffects.init();
      setIsInitialized(true);
    }
  };

  const handleHover = () => {
    initSound();
    if (soundEnabled) {
      soundEffects.playWhoosh();
    }
  };

  const handleClick = () => {
    initSound();
    if (soundEnabled) {
      soundEffects.playWaterDrop();
    }
  };

  const toggleSound = (e) => {
    e.stopPropagation();
    initSound();
    setSoundEnabled(!soundEnabled);
    soundEffects.setEnabled(!soundEnabled);
    if (!soundEnabled) {
      soundEffects.playNotification();
    }
  };

  const speedClass = {
    slow: "animate-scroll-slow",
    normal: "animate-scroll",
    fast: "animate-scroll-fast",
  }[speed];

  const items = [
    { text: SLOGANS.secondary, gradient: "from-amber-400 to-yellow-500" },
    { text: "★", gradient: "from-amber-400 to-yellow-500", isIcon: true },
    { text: SLOGANS.primary, gradient: "from-cyan-400 to-blue-500" },
    { text: "✦", gradient: "from-cyan-400 to-blue-500", isIcon: true },
    { text: t("slogan_ticker.premium_detailing"), gradient: "from-purple-400 to-pink-500" },
    { text: "✧", gradient: "from-purple-400 to-pink-500", isIcon: true },
    { text: BRAND.website, gradient: "from-cyan-400 to-blue-500" },
    { text: "★", gradient: "from-amber-400 to-yellow-500", isIcon: true },
  ];

  const allItems = [...items, ...items, ...items, ...items];

  return (
    <div 
      className="relative overflow-hidden py-3 bg-slate-950 cursor-pointer group"
      onMouseEnter={handleHover}
      onClick={handleClick}
    >
      {/* Glow effects */}
      <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 via-transparent to-cyan-500/5" />
      
      {/* Sound Toggle Button */}
      <button
        onClick={toggleSound}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-1.5 rounded-full bg-slate-800/80 text-slate-400 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
        title={soundEnabled ? t("slogan_ticker.mute_sounds") : t("slogan_ticker.enable_sounds")}
      >
        {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
      </button>
      
      <div className={`flex w-max ${speedClass}`}>
        {allItems.map((item, tickerIdx) => (
          <div key={`ticker-${tickerIdx}`} className="flex shrink-0 items-center px-6 whitespace-nowrap">
            <span className={`text-transparent bg-clip-text bg-gradient-to-r ${item.gradient} ${
              item.isIcon ? "text-lg" : "text-sm font-bold tracking-wider uppercase"
            }`}>
              {item.text}
            </span>
          </div>
        ))}
      </div>
      
      {/* Edge fade */}
      <div className="absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-slate-950 to-transparent pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-slate-950 to-transparent pointer-events-none" />
    </div>
  );
};

// Dual Line Ticker - Two rows scrolling in opposite directions with sound
export const DualLineTicker = () => {
  const { t } = useTranslation();
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  const initSound = () => {
    if (!isInitialized) {
      soundEffects.init();
      setIsInitialized(true);
    }
  };

  const handleHover = () => {
    initSound();
    if (soundEnabled) {
      soundEffects.playCarWash();
    }
  };

  const handleClick = () => {
    initSound();
    if (soundEnabled) {
      soundEffects.playSuccess();
    }
  };

  const toggleSound = (e) => {
    e.stopPropagation();
    initSound();
    setSoundEnabled(!soundEnabled);
    soundEffects.setEnabled(!soundEnabled);
    if (!soundEnabled) {
      soundEffects.playNotification();
    }
  };

  const line1Items = [
    SLOGANS.secondary,
    "★",
    t("slogan_ticker.premium_car_wash"),
    "✦",
    "Boat Detailing",
    "★",
    "RV Washing",
    "✦",
    t("slogan_ticker.fleet_services"),
    "★",
  ];

  const line2Items = [
    SLOGANS.primary,
    "✧",
    BRAND.website,
    "✦",
    t("slogan_ticker.mobile_service"),
    "✧",
    "5-Star Rated",
    "✦",
    t("slogan_ticker.insured_verified"),
    "✧",
  ];

  const allLine1 = [...line1Items, ...line1Items, ...line1Items];
  const allLine2 = [...line2Items, ...line2Items, ...line2Items];

  return (
    <div 
      className="overflow-hidden bg-slate-900 border-y border-white/10 cursor-pointer relative group"
      onMouseEnter={handleHover}
      onClick={handleClick}
    >
      {/* Sound Toggle Button */}
      <button
        onClick={toggleSound}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-1.5 rounded-full bg-slate-800/80 text-slate-400 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
        title={soundEnabled ? t("slogan_ticker.mute_sounds") : t("slogan_ticker.enable_sounds")}
      >
        {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
      </button>

      {/* Line 1 - Scrolls left */}
      <div className="py-1.5 border-b border-white/5">
        <div className="flex w-max animate-scroll">
          {allLine1.map((item, tickerIdx) => (
            <span key={`ticker-${tickerIdx}`} className="shrink-0 px-4 text-amber-400 text-xs font-bold tracking-wider uppercase whitespace-nowrap">
              {item}
            </span>
          ))}
        </div>
      </div>
      
      {/* Line 2 - Scrolls right */}
      <div className="py-1.5">
        <div className="flex w-max animate-scroll-reverse">
          {allLine2.map((item, tickerIdx) => (
            <span key={`ticker-${tickerIdx}`} className="shrink-0 px-4 text-cyan-400 text-xs font-medium tracking-wide uppercase whitespace-nowrap">
              {item}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SloganTicker;
